import { configure } from "vee-validate";
import { localize, setLocale } from "@vee-validate/i18n";
import it from "../../node_modules/@vee-validate/i18n/dist/locale/it.json";  //"@vee-validate/i18n/dist/locale/it.json";
import { defineRule } from "vee-validate";
import {
  required,
  email,
  min,
  max_value,
  min_value,
  max,
  numeric,
  length,
  between,
  regex
} from "@vee-validate/rules";
import { instance } from "../api";
import api from "../api";
import { identity, isEmpty, isNil, overSome, stubTrue } from "lodash";
import { prefix } from "@/api/helpers";
import { useRegisterFormTools } from "@/composables/forms/useRegisterFormTools.js";
import { parse, parseISO } from "date-fns";

defineRule("required", required);
defineRule("email", email);
// For length
defineRule("min", min);
defineRule("max", max);
defineRule("length", length);

// For numbers
defineRule("max_value", max_value);
defineRule("min_value", min_value);
defineRule("numeric", numeric);
defineRule("between", between);
defineRule("regex", regex);

defineRule('vat_complete', async (value, params, ctx) => {
  if (!value || value.trim() === '') {
    return true;
  }

  const [orgId, encodedRegex] = params;
  const regex = encodedRegex ? encodedRegex.replace(/\|\|\|/g, '|') : '';

  // 2. Validazione Backend
  try {
    const result = await useRegisterFormTools.validateVat(value, orgId || null);

    // Controlla se la risposta è "OK"
    if (result === "OK" || result === "ok" || result?.toLowerCase() === "ok") {
      return true;
    }

    // Un 200 con corpo diverso da "OK" non è previsto: è un'anomalia
    return useRegisterFormTools.vatErrorMessage();

  } catch (error) {
    return useRegisterFormTools.vatErrorMessage(error);
  }
});

/** VAT format: 8–14 chars (service-utils vat.constants). */
defineRule('vat_length', (value) => {
  if (!value || typeof value !== 'string') return true;
  const v = value.trim();
  if (v.length === 0) return true;
  if (v.length >= 8 && v.length <= 14) return true;
  return 'La P.IVA deve essere tra 8 e 14 caratteri';
});

/** VAT validity via Pillars service-utils (EU VIES). */
defineRule('vatVies', async (value) => {
  if (!value || typeof value !== 'string' || value.trim() === '') return true;
  const v = value.trim();
  if (v.length < 8 || v.length > 14) return true; // let vat_length handle
  try {
    const res = await api.vat.validate(v);
    const valid = res?.valid === true || res?.isValid === true;
    return valid || (typeof res?.message === 'string' ? res.message : 'P.IVA non valida (VIES)');
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message;
    return msg && typeof msg === 'string' ? msg : 'P.IVA non valida (VIES)';
  }
});

const customRules = {
  userConfirmPassword: (value, [target], ctx) => {
    if (value === ctx.form[target]) {
      return true;
    }
    return 'Passwords non coincidenti';
  },

  userUniqueEmail: (email, { excluding, mapper = identity } = {}) => {
    return instance
      .post(`${prefix}/profile/validate`, {
        type: "userUniqueEmail",
        value: mapper(email),
        excluding,
      })
      .then(stubTrue)
      .catch((res) => {
        return `Email già in uso`;
      })
  },
  confirmed: (value, { target }, { field }) => {
    if (!overSome([isNil, isEmpty])(value)) {
      const x = value === target ? true : `Il campo ${field} non corrisponde`
      return x;
    }
    return true;
  },

  userUniquePhone: (phone, { excluding, mapper = identity } = {}) =>
    instance
      .post(`${prefix}/profile/validate`, {
        type: "userUniquePhone",
        value: mapper(phone),
        excluding,
      })
      .then(stubTrue)
      .catch((res) => {
        return `Numero di telefono non valido`;
      }),
  phoneNumber: (string) => {
    let match = /^3\d{8,9}$/.test(string);
    return match
      ? true
      : "Deve essere un numero di telefono valido senza prefisso internazionale.";
  },
  codiceFiscale: (string) => {
    const fiscalCodeRegex = /^(?:[A-Z][AEIOU][AEIOUX]|[AEIOU]X{2}|[B-DF-HJ-NP-TV-Z]{2}[A-Z]){2}(?:[\dLMNP-V]{2}(?:[A-EHLMPR-T](?:[04LQ][1-9MNP-V]|[15MR][\dLMNP-V]|[26NS][0-8LMNP-U])|[DHPS][37PT][0L]|[ACELMRT][37PT][01LM]|[AC-EHLMPR-T][26NS][9V])|(?:[02468LNQSU][048LQU]|[13579MPRTV][26NS])B[26NS][9V])(?:[A-MZ][1-9MNP-V][\dLMNP-V]{2}|[A-M][0L](?:[1-9MNP-V][\dLMNP-V]|[0L][1-9MNP-V]))[A-Z]$/i;
    let match = fiscalCodeRegex.test(string);
    return match
      ? true
      : "Il codice fiscale non è valido.";
  },
  dateBefore: (date, { target, formatString }) => {
    if (date && target) {
      let currentDate = date;
      let otherDate = target;
      if (formatString) {
        if (date) currentDate = parse(date, formatString, new Date());
        if (target) otherDate = parse(target, formatString, new Date());
      } else if (typeof date === "string") {
        if (date) currentDate = parseISO(date);
        if (target) otherDate = parseISO(target);
      }
      if (currentDate > otherDate)
        return `La data non può essere maggiore del suo corrispondente`;
    }
    return true;
  },
  dateAfter: (date, { target, formatString }) => {
    if (date && target) {
      let currentDate = date;
      let otherDate = target;
      if (formatString) {
        if (date) currentDate = parse(date, formatString, new Date());
        if (target) otherDate = parse(target, formatString, new Date());
      } else if (typeof date === "string") {
        if (date) currentDate = parseISO(date);
        if (target) otherDate = parseISO(target);
      }
      if (currentDate < otherDate)
        return `La data non può essere minore del suo corrispondente`;
    }
    return true;
  },
  lt: (value, { target }) => {
    if (!isNil(value) && !isNil(target)) {
      if (+value >= +target)
        return `Il valore deve essere inferiore a ${target}`;
    }
    return true;
  },
  lte: (value, { target }) => {
    if (!isNil(value) && !isNil(target)) {
      if (+value > +target)
        return `Il valore deve essere inferiore o uguale a ${target}`;
    }
    return true;
  },
  gt: (value, { target }) => {
    if (!isNil(value) && !isNil(target)) {
      if (+value <= +target)
        return `Il valore deve essere maggiore a ${target}`;
    }
    return true;
  },
  gte: (value, { target }) => {
    if (!isNil(value) && !isNil(target)) {
      if (+value < +target)
        return `Il valore deve essere maggiore o uguale a ${target}`;
    }
    return true;
  },
};

Object.entries(customRules).forEach(([name, rule]) => defineRule(name, rule));

configure({
  generateMessage: localize({
    it,
  })
});

setLocale("it");
